import React, { useEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';

type PerfSample = {
  ts: number;
  fps?: number;
  frameTimeMs?: number;
  jsHeapUsedMB?: number;
  jsHeapTotalMB?: number;
  jsHeapLimitMB?: number;
  geometries?: number;
  textures?: number;
  programs?: number;
  drawCalls?: number;
  triangles?: number;
  overBudget?: boolean;
};

// Frame budget thresholds (milliseconds per frame)
const FRAME_BUDGET = {
  VR_90FPS: 11.1,
  VR_72FPS: 13.9,
  DESKTOP_60FPS: 16.67,
};

function mb(bytes: number) {
  return Math.round((bytes / 1024 / 1024) * 10) / 10;
}

function getPerfEnabled() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('perf') === '1') return true;
    return localStorage.getItem('gx-perf-log') === '1';
  } catch {
    return false;
  }
}

declare global {
  interface Window {
    __gxPerfLog?: PerfSample[];
    gxPerfExport?: () => string;
    gxPerfClear?: () => void;
    gxPerfEnable?: () => void;
    gxPerfDisable?: () => void;
    gxPerfBudget?: () => { avgFrameMs: number; overBudgetPct: number; drawCalls: number };
  }
}

export const ResourceLogger: React.FC<{
  enabled?: boolean;
  intervalMs?: number;
  maxSamples?: number;
}> = ({ enabled, intervalMs = 2000, maxSamples = 240 }) => {
  const { gl } = useThree();

  const isEnabled = useMemo(() => enabled ?? getPerfEnabled(), [enabled]);

  // FPS + frame time sampling
  const fpsRef = useRef<{ last: number; frames: number; fps?: number; frameTimes: number[] }>({
    last: performance.now(),
    frames: 0,
    frameTimes: [],
  });

  // Track consecutive over-budget frames for alerts
  const budgetRef = useRef<{ overBudgetCount: number; alertLogged: boolean }>({
    overBudgetCount: 0,
    alertLogged: false,
  });

  useFrame(() => {
    if (!isEnabled || (window as any).__vrOptimizedMode) return;
    const now = performance.now();
    const frameTime = now - fpsRef.current.last;

    // Only track frame times that are plausible (not the first frame or tab switches)
    if (frameTime > 0 && frameTime < 200) {
      fpsRef.current.frameTimes.push(frameTime);
      if (fpsRef.current.frameTimes.length > 60) {
        fpsRef.current.frameTimes.shift();
      }
    }

    fpsRef.current.frames += 1;
    const elapsed = now - fpsRef.current.last;
    if (elapsed >= 500) {
      fpsRef.current.fps = (fpsRef.current.frames / elapsed) * 1000;
      fpsRef.current.frames = 0;
      fpsRef.current.last = now;
    } else {
      fpsRef.current.last = now;
    }

    // Frame budget alert (warn if >50% of recent frames are over VR budget)
    const avgFrameTime = fpsRef.current.frameTimes.length > 0
      ? fpsRef.current.frameTimes.reduce((a, b) => a + b, 0) / fpsRef.current.frameTimes.length
      : 0;

    if (avgFrameTime > FRAME_BUDGET.VR_72FPS) {
      budgetRef.current.overBudgetCount++;
      if (budgetRef.current.overBudgetCount > 30 && !budgetRef.current.alertLogged) {
        console.warn(`[Perf] Frame budget exceeded: avg ${avgFrameTime.toFixed(1)}ms/frame (budget: ${FRAME_BUDGET.VR_72FPS}ms for 72fps VR). Draw calls: ${gl.info.render?.calls}`);
        budgetRef.current.alertLogged = true;
      }
    } else {
      budgetRef.current.overBudgetCount = 0;
      budgetRef.current.alertLogged = false;
    }
  });

  useEffect(() => {
    if (!isEnabled) return;

    if (!window.__gxPerfLog) window.__gxPerfLog = [];

    const exportFn = () => JSON.stringify(window.__gxPerfLog ?? [], null, 2);
    const clearFn = () => {
      window.__gxPerfLog = [];
    };
    const enableFn = () => {
      try {
        localStorage.setItem('gx-perf-log', '1');
        window.location.reload();
      } catch {
        // ignore
      }
    };
    const disableFn = () => {
      try {
        localStorage.removeItem('gx-perf-log');
        window.location.reload();
      } catch {
        // ignore
      }
    };
    const budgetFn = () => {
      const times = fpsRef.current.frameTimes;
      const avg = times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : 0;
      const overBudget = times.filter((t) => t > FRAME_BUDGET.VR_72FPS).length;
      return {
        avgFrameMs: Math.round(avg * 10) / 10,
        overBudgetPct: times.length > 0 ? Math.round((overBudget / times.length) * 100) : 0,
        drawCalls: gl.info.render?.calls ?? 0,
      };
    };

    window.gxPerfExport = exportFn;
    window.gxPerfClear = clearFn;
    window.gxPerfEnable = enableFn;
    window.gxPerfDisable = disableFn;
    window.gxPerfBudget = budgetFn;

    const id = window.setInterval(() => {
      const sample: PerfSample = { ts: Date.now() };

      // JS heap (Chromium only)
      const mem = (performance as any).memory as
        | { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number }
        | undefined;
      if (mem) {
        sample.jsHeapUsedMB = mb(mem.usedJSHeapSize);
        sample.jsHeapTotalMB = mb(mem.totalJSHeapSize);
        sample.jsHeapLimitMB = mb(mem.jsHeapSizeLimit);
      }

      // WebGL renderer stats
      const info = gl.info;
      sample.geometries = info.memory?.geometries;
      sample.textures = info.memory?.textures;
      sample.programs = (info as any).programs?.length;
      sample.drawCalls = info.render?.calls;
      sample.triangles = info.render?.triangles;

      // FPS + frame time
      if (typeof fpsRef.current.fps === 'number') sample.fps = Math.round(fpsRef.current.fps * 10) / 10;
      const times = fpsRef.current.frameTimes;
      if (times.length > 0) {
        sample.frameTimeMs = Math.round((times.reduce((a, b) => a + b, 0) / times.length) * 10) / 10;
        sample.overBudget = sample.frameTimeMs > FRAME_BUDGET.VR_72FPS;
      }

      const log = window.__gxPerfLog ?? [];
      log.push(sample);
      if (log.length > maxSamples) log.splice(0, log.length - maxSamples);
      window.__gxPerfLog = log;

      // eslint-disable-next-line no-console
      console.log('[Perf]', {
        fps: sample.fps,
        frameMs: sample.frameTimeMs,
        heapUsedMB: sample.jsHeapUsedMB,
        textures: sample.textures,
        geometries: sample.geometries,
        drawCalls: sample.drawCalls,
        triangles: sample.triangles,
        ...(sample.overBudget ? { OVER_BUDGET: true } : {}),
      });
    }, intervalMs);

    return () => {
      window.clearInterval(id);
    };
  }, [gl, intervalMs, isEnabled, maxSamples]);

  return null;
};
